import { useMemo, useRef, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { EditableCell } from '../components/EditableCell';
import { allHeaders } from '../lib/scheduleHeaders';
import { ValidationError } from '../lib/types';
import { cn } from '../lib/cn';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/tooltip';


interface VirtualValidationTableProps {
  data: Record<string, any>[];
  errors: ValidationError[];
  showOnlyErrors: boolean;
  onCellChange: (rowIndex: number, columnKey: string, newValue: string) => void;
}

const ROW_HEIGHT = 34;
const HEADER_HEIGHT = 40;

const COL_ROW = 52;
const COL_STATUS = 44;

const colWidths: Record<string, number> = {
  'EMPLOYER CODE': 140,
  'For The Month Of': 130,
  'Year Of Contribution': 140,
  'Staff ID': 100,
  'RSA PIN': 150,
  'Name of Employee': 180,
  'Normal contribution for employee': 200,
  'Normal contribution for employer': 200,
  'Voluntary contribution by employee': 210,
  'Voluntary contribution by employer': 210,
  'Other Contribution': 150,
  'TOTAL AMT.(Naira)': 140,
  'PFA code': 100,
};

const DEFAULT_COL_WIDTH = 140;

const totalRowWidth =
  COL_ROW +
  COL_STATUS +
  allHeaders.reduce((sum, h) => sum + (colWidths[h] ?? DEFAULT_COL_WIDTH), 0);

export function VirtualValidationTable({
  data,
  errors,
  showOnlyErrors,
  onCellChange,
}: VirtualValidationTableProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const errorMap = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const err of errors) {
      const key = `${err.rowIndex}:${err.columnKey}`;
      const list = map.get(key) ?? [];
      if (!list.includes(err.errorMessage)) list.push(err.errorMessage);
      map.set(key, list);
    }
    return map;
  }, [errors]);

  const rowsWithErrors = useMemo(() => {
    const set = new Set<number>();
    for (const err of errors) set.add(err.rowIndex);
    return set;
  }, [errors]);

  const displayIndices = useMemo(() => {
    if (!showOnlyErrors) return data.map((_, i) => i);
    return data.map((_, i) => i).filter(i => rowsWithErrors.has(i));
  }, [data, showOnlyErrors, rowsWithErrors]);

  const rowVirtualizer = useVirtualizer({
    count: displayIndices.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 20,
    paddingStart: HEADER_HEIGHT,
  });

  const getCellErrors = useCallback(
    (rowIndex: number, col: string) => errorMap.get(`${rowIndex}:${col}`) ?? [],
    [errorMap]
  );

  if (displayIndices.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <CheckCircle2 className="w-12 h-12 text-emerald-500" />
        <p className="text-base font-semibold text-emerald-700">No errors found</p>
        <p className="text-sm text-slate-400">All rows pass validation.</p>
      </div>
    );
  }

  const totalHeight = rowVirtualizer.getTotalSize();
  const virtualItems = rowVirtualizer.getVirtualItems();

  return (
    <div className="flex flex-col" style={{ maxHeight: '75vh' }}>
      <div
        ref={scrollRef}
        className="overflow-auto flex-1"
        style={{ maxHeight: 'calc(75vh - 40px)' }}
      >
        <div style={{ position: 'relative', height: totalHeight, minWidth: totalRowWidth }}>
          {/* Sticky header */}
          <div
            style={{
              position: 'sticky',
              top: 0,
              left: 0,
              zIndex: 10,
              display: 'flex',
              height: HEADER_HEIGHT,
              minWidth: totalRowWidth,
              background: '#1e293b',
              color: 'white',
            }}
          >
            <div
              style={{ width: COL_ROW, minWidth: COL_ROW, flexShrink: 0 }}
              className="flex items-center px-2 text-xs font-medium border-r border-slate-600"
            >
              Row
            </div>
            <div
              style={{ width: COL_STATUS, minWidth: COL_STATUS, flexShrink: 0 }}
              className="flex items-center justify-center text-xs font-medium border-r border-slate-600"
            >
              <span className="sr-only">Status</span>
            </div>
            {allHeaders.map(h => (
              <div
                key={h}
                style={{ width: colWidths[h] ?? DEFAULT_COL_WIDTH, minWidth: colWidths[h] ?? DEFAULT_COL_WIDTH, flexShrink: 0 }}
                className="flex items-center px-2 text-xs font-medium whitespace-nowrap border-r border-slate-600"
              >
                {h}
              </div>
            ))}
          </div>

          {/* Virtual rows */}
          {virtualItems.map(virtualRow => {
            const originalIndex = displayIndices[virtualRow.index];
            const row = data[originalIndex];
            const rowHasError = rowsWithErrors.has(originalIndex);
            const rowErrorCount = allHeaders.filter(h => getCellErrors(originalIndex, h).length > 0).length;

            return (
              <div
                key={virtualRow.key}
                data-index={virtualRow.index}
                ref={rowVirtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  transform: `translateY(${virtualRow.start}px)`,
                  display: 'flex',
                  height: ROW_HEIGHT,
                  minWidth: totalRowWidth,
                }}
                className={cn(
                  'border-b border-gray-100',
                  rowHasError
                    ? 'bg-red-50/70'
                    : virtualRow.index % 2 === 0
                    ? 'bg-white'
                    : 'bg-slate-50/40',
                )}
              >
                <div
                  style={{ width: COL_ROW, minWidth: COL_ROW, flexShrink: 0 }}
                  className="flex items-center justify-center text-xs font-mono text-slate-400 border-r border-gray-100 bg-slate-50/80"
                >
                  {originalIndex + 2}
                </div>

                <div
                  style={{ width: COL_STATUS, minWidth: COL_STATUS, flexShrink: 0 }}
                  className="flex items-center justify-center border-r border-gray-100"
                >
                  {rowHasError ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="flex items-center gap-0.5 cursor-default select-none">
                          <AlertCircle className="w-3.5 h-3.5 text-red-500" />
                          <span className="text-xs font-semibold text-red-600">{rowErrorCount}</span>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <p className="text-xs bg-white p-2">{rowErrorCount} field{rowErrorCount !== 1 ? 's' : ''} with error</p>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                  )}
                </div>

                {allHeaders.map(header => {
                  const cellErrors = getCellErrors(originalIndex, header);
                  const hasError = cellErrors.length > 0;
                  return (
                    <div
                      key={header}
                      style={{ width: colWidths[header] ?? DEFAULT_COL_WIDTH, minWidth: colWidths[header] ?? DEFAULT_COL_WIDTH, flexShrink: 0 }}
                      className={cn(
                        'flex items-center border-r border-gray-100 px-1',
                        hasError ? 'bg-red-50' : '',
                      )}
                    >
                      <EditableCell
                        value={String(row[header] ?? '')}
                        hasError={hasError}
                        errors={cellErrors}
                        onChange={(val) => onCellChange(originalIndex, header, val)}
                      />
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      <div className="border-t border-slate-200 px-4 py-2 bg-slate-50 text-xs text-slate-500 flex-shrink-0">
        Showing <strong>{displayIndices.length.toLocaleString()}</strong> of{' '}
        <strong>{data.length.toLocaleString()}</strong> rows
        {showOnlyErrors && errors.length > 0 ? ' (rows with errors only)' : ''}
      </div>
    </div>
  );
}
